/**
 * TransactionsDrillDialog — click a bar on the Dashboard → this dialog
 * opens with every transaction that rolls up into that bar, editable in
 * place the same way the Review "All as table" view is.
 *
 * Filters supported:
 *   • costCentre  – dashboard's current cost centre (or "" for Total)
 *   • category    – required (the category the bar represents)
 *   • month       – "YYYY-MM" (if provided, narrows to a single month)
 *
 * The dialog fetches its own list so it stays independent from the
 * Review tab's local state. Saving a change hits the same endpoint the
 * Review tab uses, so both stay in sync on next reload.
 */
import { useCallback, useEffect, useState, useMemo } from "react";
import { Loader2, RotateCcw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  listTransactions, updateTransaction, deleteTransaction, money,
} from "@/lib/operations/api";

function longMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

export default function TransactionsDrillDialog({
  open, onOpenChange,
  costCentre,
  category,
  month,        // "YYYY-MM" | null (null = across all months)
  config,
  onChanged,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Manual vs statement source filter, scoped to this dialog.
  const [sourceFilter, setSourceFilter] = useState("");
  // Subcategory filter — only meaningful when the parent category has a
  // registered subcategory set (e.g. Technology → hosting/development/
  // video). "" = show all, "__none__" = only rows with no subcategory.
  const [subFilter, setSubFilter] = useState("");

  // Reset the subcategory filter whenever the drill target changes.
  useEffect(() => { setSubFilter(""); }, [category, month]);

  // Hide the floating Hope widget while the dialog is up.
  useEffect(() => {
    if (!open) return undefined;
    document.body.dataset.hideHope = "1";
    return () => { delete document.body.dataset.hideHope; };
  }, [open]);

  const reload = useCallback(async () => {
    if (!open || !category) return;
    setLoading(true);
    try {
      const params = {};
      if (costCentre) params.cost_centre = costCentre;
      const list = await listTransactions(params);
      // Client-side narrow to category + month
      const filtered = list.filter((t) => {
        if ((t.category || "") !== category) return false;
        if (month && (t.date || "").slice(0, 7) !== month) return false;
        return true;
      });
      setRows(filtered);
    } catch {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [open, costCentre, category, month]);

  useEffect(() => { reload(); }, [reload]);

  const catLabel = useMemo(
    () => config?.categories?.find((c) => c.key === category)?.label || category,
    [config, category],
  );

  // Registered subcategories for the current category (e.g. Technology).
  // Empty when the category doesn't have subcategories.
  const subs = useMemo(
    () => (config?.subcategories?.[category] || []),
    [config, category],
  );
  const subLabel = useMemo(() => {
    const map = {};
    subs.forEach((s) => { map[s.key] = s.label; });
    return map;
  }, [subs]);

  // Per-subcategory totals for the current row set. Used to render the
  // count/total next to each filter chip so the operator can see at a
  // glance what's inside each bucket before drilling further.
  const subTotals = useMemo(() => {
    const out = { __none__: { count: 0, total: 0 } };
    subs.forEach((s) => { out[s.key] = { count: 0, total: 0 }; });
    rows.forEach((t) => {
      const key = t.subcategory || "__none__";
      if (!out[key]) out[key] = { count: 0, total: 0 };
      out[key].count += 1;
      out[key].total += Number(t.amount || 0);
    });
    return out;
  }, [rows, subs]);

  // Apply the local source filter (manual vs statement) on top of what the
  // API returned. Rows without a `source` field are assumed to be imports.
  const visibleRows = useMemo(() => {
    return rows.filter((t) => {
      if (sourceFilter && (t.source || "import") !== sourceFilter) return false;
      if (subFilter) {
        const s = t.subcategory || "";
        if (subFilter === "__none__" ? s !== "" : s !== subFilter) return false;
      }
      return true;
    });
  }, [rows, sourceFilter, subFilter]);

  const total = visibleRows.reduce((s, r) => s + Number(r.amount || 0), 0);

  const patch = async (id, body) => {
    try {
      await updateTransaction(id, body);
      setRows((r) => r.map((t) => (t.id === id ? { ...t, ...body } : t)));
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this transaction? This cannot be undone.")) return;
    try {
      await deleteTransaction(id);
      setRows((r) => r.filter((t) => t.id !== id));
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // Flip a row back to pending review so the operator can reclassify it
  // without deleting or hand-editing category/cost centre. Sends the row
  // straight to the Review queue on next reload.
  const sendBackToReview = async (id) => {
    try {
      await updateTransaction(id, { reviewed: false });
      setRows((r) => r.filter((t) => t.id !== id));
      onChanged?.();
      toast.success("Sent back to review");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send-back failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl p-0 overflow-hidden flex flex-col top-3 translate-y-0 sm:top-[50%] sm:translate-y-[-50%] max-h-[calc(100dvh-1.5rem)] sm:max-h-[92vh] z-[90]"
        data-testid="ops-drill-dialog"
      >
        <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 shrink-0 border-b border-slate-100">
          <DialogTitle className="text-brand-navy text-base sm:text-lg leading-tight pr-8">
            {catLabel}
            {month ? <span className="text-slate-500 font-medium"> · {longMonth(month)}</span> : null}
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1 tabular-nums">
            {loading ? "Loading…" : `${visibleRows.length} transactions · ${money(total)}`}
          </p>
          {/* Source filter — Statement vs Manual */}
          <div className="mt-3 inline-flex items-center rounded-full bg-slate-100 border border-slate-200 p-0.5 self-start" data-testid="ops-drill-source-toggle">
            {[
              { key: "", label: "All" },
              { key: "import", label: "Statement" },
              { key: "manual", label: "Manual" },
            ].map((opt) => {
              const active = sourceFilter === opt.key;
              return (
                <button
                  key={opt.key || "all"}
                  type="button"
                  onClick={() => setSourceFilter(opt.key)}
                  className={`h-6 px-2.5 rounded-full text-[11px] font-semibold transition ${
                    active
                      ? "bg-brand-navy text-white shadow-sm"
                      : "text-brand-navy/70 hover:text-brand-navy"
                  }`}
                  data-testid={`ops-drill-source-${opt.key || "all"}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {/* Subcategory filter — only rendered when the parent category
              actually has registered subcategories (Technology today).
              Single-line horizontal scroll (like the Sponsor row) keeps
              the chip strip clean instead of wrapping into a messy grid. */}
          {subs.length > 0 && (
            <div
              className="mt-2 -mx-5 sm:-mx-6 px-5 sm:px-6 overflow-x-auto hide-scrollbar"
              data-testid="ops-drill-sub-toggle"
            >
              <div className="flex items-center gap-1.5 w-max">
                {[
                  { key: "", label: "All", showTotal: false },
                  ...subs.map((s) => ({ key: s.key, label: s.label, showTotal: true })),
                  { key: "__none__", label: "Unassigned", showTotal: true },
                ]
                  .filter((opt) =>
                    opt.key === "" ||
                    opt.key === subFilter ||
                    opt.key !== "__none__" ||
                    (subTotals.__none__?.count || 0) > 0,
                  )
                  .map((opt) => {
                    const active = subFilter === opt.key;
                    const bucket = subTotals[opt.key];
                    return (
                      <button
                        key={opt.key || "__all__"}
                        type="button"
                        onClick={() => setSubFilter(opt.key)}
                        className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-semibold border transition tabular-nums whitespace-nowrap shrink-0 ${
                          active
                            ? "bg-brand-navy text-white border-brand-navy shadow-sm"
                            : "bg-white text-brand-navy/80 border-slate-200 hover:border-brand-navy/40 hover:text-brand-navy"
                        }`}
                        data-testid={`ops-drill-sub-${opt.key || "all"}`}
                      >
                        <span>{opt.label}</span>
                        {opt.showTotal && bucket && (
                          <span className={active ? "text-white/80 font-medium" : "text-slate-500 font-medium"}>
                            · {money(bucket.total)}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
              No transactions in this bucket.
            </div>
          ) : (
            <table className="text-sm min-w-[1120px]">
              <thead className="bg-slate-50 text-slate-500 sticky top-0 z-10">
                <tr className="whitespace-nowrap">
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Date</th>
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Description</th>
                  <th className="text-right font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Amount</th>
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Company</th>
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Category</th>
                  {subs.length > 0 && (
                    <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Subcategory</th>
                  )}
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Cost centre</th>
                  <th className="text-left font-semibold px-3 py-2 text-[10px] uppercase tracking-wider">Loan</th>
                  <th className="w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {visibleRows.map((t) => (
                  <tr key={t.id} className="whitespace-nowrap" data-testid={`ops-drill-row-${t.id}`}>
                    <td className="px-3 py-2 tabular-nums text-slate-600">{t.date}</td>
                    <td className="px-3 py-2 text-brand-navy font-medium max-w-[360px] truncate" title={t.description}>
                      <span className="inline-flex items-center gap-1.5">
                        {t.source === "manual" && (
                          <span
                            className="inline-flex items-center px-1.5 h-4 rounded bg-brand-teal/10 text-brand-teal text-[9px] font-bold tracking-wide shrink-0"
                            title="Manually entered (not from a statement)"
                          >
                            M
                          </span>
                        )}
                        <span className="truncate">{t.description}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-brand-navy">
                      {money(t.amount)}
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {t.company || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={t.category || ""}
                        onChange={(e) => patch(t.id, { category: e.target.value })}
                        className="h-8 w-40 rounded-md border border-slate-200 bg-white px-2 text-sm"
                        data-testid={`ops-drill-cat-${t.id}`}
                      >
                        {config.categories.map((c) => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>
                    </td>
                    {subs.length > 0 && (
                      <td className="px-3 py-2">
                        {/* Only rows still in the parent category can pick a
                            subcategory — moving a row out of the category
                            hides the picker on next render. */}
                        {(t.category || "") === category ? (
                          <select
                            value={t.subcategory || ""}
                            onChange={(e) => patch(t.id, { subcategory: e.target.value || "" })}
                            className="h-8 w-36 rounded-md border border-slate-200 bg-white px-2 text-sm"
                            data-testid={`ops-drill-sub-${t.id}`}
                          >
                            <option value="">—</option>
                            {subs.map((s) => (
                              <option key={s.key} value={s.key}>{s.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <select
                        value={t.cost_centre || ""}
                        onChange={(e) => patch(t.id, { cost_centre: e.target.value })}
                        className="h-8 w-36 rounded-md border border-slate-200 bg-white px-2 text-sm"
                        data-testid={`ops-drill-cc-${t.id}`}
                      >
                        <option value="">—</option>
                        {config.cost_centres.map((c) => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={t.is_loan ? "yes" : "no"}
                        onChange={(e) => patch(t.id, { is_loan: e.target.value === "yes" })}
                        className={`h-8 w-20 rounded-md border px-2 text-sm ${
                          t.is_loan
                            ? "border-brand-teal/50 bg-brand-teal/10 text-brand-teal-dark font-semibold"
                            : "border-slate-200 bg-white"
                        }`}
                        data-testid={`ops-drill-loan-${t.id}`}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => sendBackToReview(t.id)}
                          className="h-7 w-7 rounded-md text-brand-teal hover:bg-brand-teal/10 flex items-center justify-center"
                          title="Send back to review"
                          data-testid={`ops-drill-reopen-${t.id}`}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRow(t.id)}
                          className="h-7 w-7 rounded-md text-red-500 hover:bg-red-50 flex items-center justify-center"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DialogFooter className="px-5 sm:px-6 py-3 border-t border-slate-100 shrink-0 bg-white flex-row justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-10"
            data-testid="ops-drill-close"
          >
            <X className="h-4 w-4 mr-1.5" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
